import React, { useState, useRef } from 'react';
import { EdgeWhitespaceNotice, fieldsWithEdgeWhitespace } from '../../utils/accountFieldHygiene';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../superadmin/AddDistrict.css';

const AddDistrictAdmin = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    title: '',
    password: '',
    role: 'district_admin'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // A parent account already uses this email. The server refuses to create a silent twin and
  // hands back the details so we can offer to link them instead.
  const [linkOffer, setLinkOffer] = useState(null);
  // disabled={loading} only takes effect after a re-render, so a fast second click can fire
  // before it applies. This ref is synchronous - the same fix the parent forms needed after
  // duplicate submissions turned up in the first week of live use.
  const submitInFlight = useRef(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const submit = async (extra = {}) => {
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    setError('');
    setLinkOffer(null);
    setLoading(true);

    try {
      await axios.post('/api/users/my-district-user', { ...formData, ...extra });
      navigate('/district-admin/admins');
    } catch (error) {
      const data = error.response?.data;
      // 409 + link_available: this person already has a parent account and can hold both.
      if (error.response?.status === 409 && data?.link_available) {
        setLinkOffer(data);
      } else {
        setError(data?.error || 'Failed to create district user');
      }
    } finally {
      submitInFlight.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    /**
     * Refused rather than silently trimmed, because the person typing is not the person who will
     * sign in. A district admin adding a colleague would never discover the stray space; the
     * colleague would just find that their username does not work. The server trims regardless,
     * so this is about the admin seeing the username they are handing over.
     */
    const dirty = fieldsWithEdgeWhitespace({ Username: formData.username, Email: formData.email });
    if (dirty.length > 0) {
      setError(`Remove the extra space at the start or end of the ${dirty.join(' and ').toLowerCase()} before saving.`);
      return;
    }
    submit();
  };

  return (
    <div className="add-district">
      <h2>Add District User</h2>
      {error && <div className="error-message">{error}</div>}

      {/*
        The person already has a parent account. Rather than a dead end, offer the thing the
        district actually wants: let them hold both, and choose which one to open at sign-in.
      */}
      {linkOffer && (
        <div className="modal-overlay" onClick={() => setLinkOffer(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{linkOffer.parent?.name || 'This person'} already has a parent account</h3>
            <p>{linkOffer.detail}</p>
            <p>
              Their parent account (<strong>{linkOffer.parent?.username}</strong>) and the
              children on it are not changed. You can remove district access later without
              affecting it.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-primary"
                disabled={loading}
                onClick={() => submit({ link_to_parent: true })}
              >
                {loading ? 'Adding…' : 'Give them district access'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setLinkOffer(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <EdgeWhitespaceNotice
            fields={{ Username: formData.username, Email: formData.email }}
            onFix={() => { setError(''); setFormData((f) => ({ ...f, username: (f.username || '').trim(), email: (f.email || '').trim() })); }}
          />

          <div className="form-group">
            <label>Username *</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Password *</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
            />
            <small>A password reset link will be sent to the email address</small>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>User Permission Level *</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
            >
              <option value="district_admin">District Admin - Full access to all district functions, can manage users and settings</option>
              <option value="district_editor">District Editor - Can view/edit student data and forms, cannot manage users or registration codes</option>
              <option value="district_user">District User - Read-only access to district and student data, can create/run reports</option>
            </select>
            <small>
              {formData.role === 'district_admin' && 'Full administrative access including user management, registration codes, and school settings.'}
              {formData.role === 'district_editor' && 'Can view and edit student data and forms. Cannot add/edit users, registration codes, or school information. Suitable for administrative assistants and secretaries.'}
              {formData.role === 'district_user' && 'Read-only access to all district and student data. Can create and run reports but cannot modify any data. Suitable for staff needing information access.'}
            </small>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create District User'}
          </button>
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={() => navigate('/district-admin/admins')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddDistrictAdmin;

